import { formatOrderRef } from '@misumi/shared'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import type { components } from '../../lib/api/schema.gen'

type CartLinePresentation = components['schemas']['CartLinePresentation']

/** Distinct source-order ids across the cart's line presentations, in first-seen
 * order, each with how many lines came from it. Reorder re-adds a whole order at
 * once, so the common case is a single order shared by every reordered line —
 * hence one cart-level notice, not a per-line caption. Exported so CartPage can
 * decide whether to also mark each line with its order (only worthwhile when the
 * cart mixes 2+ distinct source orders). */
export function distinctSourceOrders(
  presentations: readonly CartLinePresentation[],
): { orderId: string; lineCount: number }[] {
  const counts = new Map<string, number>()
  for (const p of presentations) {
    if (p.sourceOrderId) counts.set(p.sourceOrderId, (counts.get(p.sourceOrderId) ?? 0) + 1)
  }
  return [...counts.entries()].map(([orderId, lineCount]) => ({ orderId, lineCount }))
}

const items = (n: number) => `${n} item${n === 1 ? '' : 's'}`

/** A link to one source order. Routes by the FULL order id (`to`), but DISPLAYS the
 * friendly "SO-#####" reference (`formatOrderRef`) rather than the raw UUID — a buyer
 * has no use for the opaque id, only the same reference the order list/detail pages show. */
function OrderLink({ orderId }: { readonly orderId: string }) {
  return (
    <Link to={`/orders/${orderId}`} className="cart-reorder-notice-link">
      order {formatOrderRef(orderId)}
    </Link>
  )
}

export interface ReorderSourceNoticeProps {
  readonly presentations: readonly CartLinePresentation[]
}

/**
 * A single cart-level notice (UID-94) telling the shopper which placed order(s)
 * lines in this cart were reordered from, each linking to that order's detail page
 * by its full order id. Rendered once above the line table instead of repeating the
 * same caption on every line (reorder adds the whole order at a time, so all its
 * lines share one order id). Renders nothing when no line carries reorder provenance.
 */
export function ReorderSourceNotice({ presentations }: ReorderSourceNoticeProps) {
  const orders = distinctSourceOrders(presentations)
  if (orders.length === 0) return null

  return (
    <div className="cart-reorder-notice" role="note">
      <span className="cart-reorder-notice-icon" aria-hidden="true">
        ↺
      </span>
      <p className="cart-reorder-notice-text">
        {orders.length === 1 ? (
          // Single order (the usual case): "N items reordered from order <full id>."
          <>
            {items(orders[0]?.lineCount ?? 0)} reordered from{' '}
            <OrderLink orderId={orders[0]?.orderId ?? ''} />.
          </>
        ) : (
          // Cart mixing several reorders: name each distinct order once, with its own
          // line count, joined with commas and a trailing "and".
          <>
            Reordered from {orders.length} orders:{' '}
            {orders.map((order, index) => (
              <Fragment key={order.orderId}>
                {index > 0 && (index === orders.length - 1 ? ' and ' : ', ')}
                <OrderLink orderId={order.orderId} /> ({items(order.lineCount)})
              </Fragment>
            ))}
            .
          </>
        )}
      </p>
    </div>
  )
}
